import classNames from 'classnames';
import { ReactNode } from 'react';

import {
    CollapsableProvider,
    useCollapsable,
} from '../../hooks/ui/useCollapsable';
import borderRadiusAsClasses from '../../shared/borderRadiusAsClasses';
import TopBottomBorderRadius from '../../types/UI/TopBottomBorderRadius';
import ToggleBar from '../ToggleBar/ToggleBar';

import styles from './Well.module.css';

type WellContentProps = {
    children: ReactNode;
};

const WellContent = ({ children }: WellContentProps) => {
    const { collapsed } = useCollapsable();
    const classes = classNames(styles.Content, {
        [styles.Content_collapsed]: collapsed,
    });

    return <div className={classes}>{children}</div>;
};

type WellHeaderProps = {
    children: ReactNode;
};

const WellHeader = ({ children }: WellHeaderProps) => {
    const { collapsed, setCollapsed, disabled } = useCollapsable();

    return (
        <ToggleBar
            disabled={disabled}
            onToggle={() => setCollapsed(!collapsed)}
            toggled={!collapsed}
        >
            {children}
        </ToggleBar>
    );
};

type WellProps = {
    className?: string;
    compact?: boolean;
    borderRadius?: TopBottomBorderRadius;
    borderBottom?: boolean;
    extraTopPadding?: boolean;
    defaultCollapsed?: boolean;
    collapsable?: boolean;
    children?: ReactNode;
};

const Well = ({
    children,
    className,
    borderRadius = 'all',
    compact = false,
    borderBottom = false,
    extraTopPadding = false,
    collapsable = false,
    defaultCollapsed = false,
}: WellProps) => {
    const classes = classNames(className, {
        [styles.Root]: true,
        [borderRadiusAsClasses(borderRadius, {})]: true,
        [styles.compact]: compact,
        [styles.borderBottom]:
            borderBottom && !['bottom', 'all'].includes(borderRadius),
        [styles.extraTopPadding]: extraTopPadding,
    });

    return (
        <div className={classes}>
            <CollapsableProvider
                defaultCollapsed={defaultCollapsed}
                disabled={!collapsable}
            >
                {children}
            </CollapsableProvider>
        </div>
    );
};

Well.Content = WellContent;
Well.Header = WellHeader;

export default Well;
